/* eslint-disable react-hooks/rules-of-hooks */
import _ from 'underscore';
import React, { useRef, memo } from 'react';
import type { NamedExoticComponent, ReactNode, WeakValidationMap } from 'react';
import PropTypes from 'prop-types';

import { EnvironmentContext } from '../environment/context';
import type { Environment } from '../environment';
import type { OverreactRequestShape } from '../public-types';

const MAX_REQUEST_BATCH_SIZE = 10;
const FETCH_INTERVAL = 50;
const INIT_MIDDLEWARE_STATES = { isResponseFromStore: false };


function executeRequests(environment: Environment, requests: OverreactRequestShape[]) {
  const pendingRequests = [
    ...requests,
  ];
  while (pendingRequests.length > 0) {
    const req = pendingRequests.shift();
    if (req) {
      const {
        requestContract,
        spec,
        variables,
        data,
        id,
        componentName,
      } = req;

      req.middlewareStates = {
        ...INIT_MIDDLEWARE_STATES,
      };

      const { responseContract } = spec;
      const {
        verb, uriFactoryFn, headerFactoryFn, payloadFactoryFn,
      } = requestContract;

      const uri = uriFactoryFn({ requestContract, variables, data });
      const header = headerFactoryFn && headerFactoryFn({ requestContract, variables, data });
      const payload = payloadFactoryFn && payloadFactoryFn({ requestContract, variables, data });

      const requestor = environment.getRequestor(
        id,
        spec,
        variables,
        req.middlewareStates,
        { componentName }
      );

      requestor(uri, verb, header || undefined, payload).execute({

        onComplete: (response: unknown) => {
          responseContract.onGetResponse(environment, response, req);
        },

        onError: (error: unknown) => {
          responseContract.onGetError(environment, req, error);
        },
      });
    }
  }
}


function batchRequests(environment: Environment, batchSize: number) {
  // console.log(`executing requests for batch size ${batchSize}`);

  // we'll try to batch requests
  const requests = [];
  while (requests.length < batchSize) {
    const request = environment.removeRequest();
    if (request) {
      requests.push(request);
    }
  }

  executeRequests(environment, requests);
}


export function useEnvironmentInitialization(environment: Environment) {
  // const [currentEnvironment, setCurrEnv] = useState(environment);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // TODO: we need better way to subscribe here
  // current way will result in memory leak or other bugs when environment change
  environment.subscribe(() => {
    if (!timer.current) {
      clearTimeout(timer.current);
    }

    if (environment.requestCount() >= MAX_REQUEST_BATCH_SIZE) {
      // immediately execute requests to reduce queue size
      batchRequests(environment, MAX_REQUEST_BATCH_SIZE);
    } else {
      // a few requests outstanding, wait a bit to accumulate more.
      timer.current = setTimeout(() => {
        batchRequests(environment, environment.requestCount());
      }, FETCH_INTERVAL);
    }
  });
}

// default top-level data fetcher
export interface DataFetcherProps {
  environment: Environment;
  environments?: Environment[] | null;
  children: ReactNode;
}

export const DataFetcher = memo<DataFetcherProps>(props => {
  const {

    environment,

    environments,
    children,
  } = props;
  useEnvironmentInitialization(environment);
  _.each(environments, env => useEnvironmentInitialization(env));

  return (
    <EnvironmentContext.Provider value={[environment, environments]}>
      {children}
    </EnvironmentContext.Provider>
  );
}) as NamedExoticComponent<DataFetcherProps> & {
  propTypes?: WeakValidationMap<DataFetcherProps>;
  defaultProps?: Partial<DataFetcherProps>;
};


DataFetcher.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  environment: PropTypes.object.isRequired,
  environments: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
} as WeakValidationMap<DataFetcherProps>;


DataFetcher.defaultProps = {
  environments: null,
};
